import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFlagsService } from '@atlassian/jira-flags';
import type { Flag, FlagCustomRenderProps } from '@atlassian/jira-flags/src/services/types';
import { useFormSubmitStatus, useBulkOperationsFormSubmissionActions } from './main.tsx';
import { SubmitFlagStatus } from './types.tsx';

export type FlagProps = {
	onDismissed: () => void;
};

export type FlagRendererProps = FlagCustomRenderProps & FlagProps;

type Props = {
	renderSuccessFlag: (props: FlagCustomRenderProps & FlagProps) => JSX.Element;
	renderInProgressFlag: (props: FlagCustomRenderProps & FlagProps) => JSX.Element;
	renderErrorFlag: (props: FlagCustomRenderProps & FlagProps) => JSX.Element;
	renderPartialErrorFlag: (props: FlagCustomRenderProps & FlagProps) => JSX.Element;
};

export const useFormStatusFlags = ({
	renderSuccessFlag,
	renderInProgressFlag,
	renderErrorFlag,
	renderPartialErrorFlag,
}: Props) => {
	const submitStatus = useFormSubmitStatus();
	const existingFlagId = useRef<string | undefined>();
	const { showFlag, dismissFlag } = useFlagsService();
	const { setIdle, dismissPollingFlag } = useBulkOperationsFormSubmissionActions();

	const dismissExistingFlag = useCallback(() => {
		if (existingFlagId.current) {
			dismissFlag(existingFlagId.current);
			existingFlagId.current = undefined;
		}
	}, [dismissFlag]);

	const flags: Record<string, Flag> = useMemo(
		() => ({
			[SubmitFlagStatus.ProgressFlag]: {
				id: 'in-progress-flag',
				render: (props: FlagCustomRenderProps) =>
					renderInProgressFlag({
						...props,
						onDismissed: dismissPollingFlag,
					}),
			},
			[SubmitFlagStatus.ErrorFlag]: {
				id: 'error-flag',
				render: (props: FlagCustomRenderProps) =>
					renderErrorFlag({
						...props,
						onDismissed: setIdle,
					}),
			},
			[SubmitFlagStatus.PartialErrorFlag]: {
				id: 'partial-error-flag',
				render: (props: FlagCustomRenderProps) =>
					renderPartialErrorFlag({
						...props,
						onDismissed: setIdle,
					}),
			},
			[SubmitFlagStatus.SuccessFlag]: {
				id: 'success-flag',
				render: (props: FlagCustomRenderProps) =>
					renderSuccessFlag({
						...props,
						onDismissed: setIdle,
					}),
			},
		}),
		[
			dismissPollingFlag,
			renderErrorFlag,
			renderInProgressFlag,
			renderPartialErrorFlag,
			renderSuccessFlag,
			setIdle,
		],
	);

	useEffect(() => dismissExistingFlag, [dismissExistingFlag]);

	useEffect(() => {
		const flag = flags[submitStatus];
		if (!flag) {
			dismissExistingFlag();
			return;
		}
		if (existingFlagId.current) {
			setTimeout(() => {
				existingFlagId.current = showFlag(flag);
				dismissFlag('in-progress-flag');
			}, 200);
		} else {
			existingFlagId.current = showFlag(flag);
		}
	}, [dismissFlag, dismissExistingFlag, setIdle, flags, submitStatus, showFlag]);
};

export const useResetFormSubmissionState = (): (() => void) => {
	const { setIdle } = useBulkOperationsFormSubmissionActions();
	return setIdle;
};
